import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { AdminSubmission, Submission } from '../../types/submission.types';
import { useSTKContext } from '@/context/context';

export const useSubmissions = (enabled: boolean) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<Submission[]>>({
    queryKey: ['submissions'],
    queryFn: () => apiClient.get<Submission[]>(`/submissions`),
    enabled,
  });
};

export const useAdminSubmissions = (enabled: boolean) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<AdminSubmission[]>>({
    queryKey: ['admin', 'submissions'],
    queryFn: () => apiClient.get<AdminSubmission[]>(`/admin/submissions`),
    enabled,
  });
};

export const useAdminSubmissionDetails = (enabled: boolean, orgId: string, submissionId?: string) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<AdminSubmission>>({
    queryKey: ['admin', 'submissions', orgId, submissionId],
    queryFn: () => apiClient.get<AdminSubmission>(`/admin/submissions/${orgId}`, { params: { submissionId } }),
    enabled,
  });
};

export const useFZSubmissionDetails = (enabled: boolean, id: string) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<Submission>>({
    queryKey: ['submissions', id],
    queryFn: () => apiClient.get<Submission>(`/submissions/${id}`, { params: { id } }),
    enabled,
  });
};

export const useCompleteSubmissions = () => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orgIds: string[]) => apiClient.put(`/admin/submissions`, orgIds),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin', 'submissions']);
      queryClient.invalidateQueries(['submissions']);
      // the following queries may contain a pending status and need to be updated
      queryClient.invalidateQueries(['stores']);
      queryClient.invalidateQueries(['bundle-groups']);
    },
  });
};
